<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { ElMessage, type FormInstance, type FormRules, type UploadFile } from 'element-plus';
import { useI18n } from 'vue-i18n';
import type { DomainInputRequestInput } from '@/types/api/User/DomainInputRequest';
import type { SupplierForAdmin } from '@/services/api/userService';
import type { MinimalPackageDto } from '@/types/api/Product/MinimalPackageDto';
import type { SchemaTypes } from '@/types/api/User/SchemaType';
import { useRouter } from 'vue-router';

const { t } = useI18n();
const router = useRouter();

const props = defineProps<{
  modelValue: DomainInputRequestInput;
  loading?: boolean;
  suppliers?: Array<SupplierForAdmin>;
  packages?: Array<MinimalPackageDto>;
}>();

const isProcessingImage = ref(false);
const wasSubmitting = ref(false);

const emit = defineEmits<{
  (e: 'update:modelValue', v: DomainInputRequestInput): void;
  (e: 'submit', v: DomainInputRequestInput): void;
}>();

const formRef = ref<FormInstance>();

const form = computed({
  get: () => {
    const f = props.modelValue;
    if (!f.colorSchemas) f.colorSchemas = [];
    if (!f.packagesIds) f.packagesIds = [];
    return f;
  },
  set: (v: DomainInputRequestInput) => emit('update:modelValue', v)
});

function getSchemaHex(schemaType: SchemaTypes): string {
  return form.value.colorSchemas.find(c => c.schemaType === schemaType)?.hexCode ?? '';
}
function setSchemaHex(schemaType: SchemaTypes, hex: string) {
  const idx = form.value.colorSchemas.findIndex(c => c.schemaType === schemaType);
  if (idx >= 0) {
    form.value.colorSchemas[idx] = { ...form.value.colorSchemas[idx], hexCode: hex || '' };
  } else {
    form.value.colorSchemas.push({ schemaType, hexCode: hex || '' });
  }
}
const colorHex = computed({
  get: () => getSchemaHex('COLOR'),
  set: (hex: string) => setSchemaHex('COLOR', hex)
});
const textHex = computed({
  get: () => getSchemaHex('TEXT'),
  set: (hex: string) => setSchemaHex('TEXT', hex)
});

const rules = computed<FormRules>(() => ({
  price: [
    {
      type: 'number',
      transform: (v: any) => (v === '' || v == null ? null : Number(v)),
      message: t('components.adminDashboard.createDomain.validation.priceNumber')
    },
    {
      validator: (_, v, cb) =>
        (v == null || v >= 0)
          ? cb()
          : cb(new Error(t('components.adminDashboard.createDomain.validation.priceMin')))
    }
  ],
  urlSuffix: [
    { min: 0, max: 64, message: t('components.adminDashboard.createDomain.validation.urlMaxLength'), trigger: 'blur' },
    { pattern: /^[a-z0-9-]*$/i, message: t('components.adminDashboard.createDomain.validation.urlPattern'), trigger: 'blur' }
  ],
  companyWebsite: [
    {
      validator: (_, v, cb) => {
        if (!v) return cb();
        try {
          const u = new URL(String(v));
          return /^(http|https):$/.test(u.protocol)
            ? cb()
            : cb(new Error(t('components.adminDashboard.createDomain.validation.urlProtocol')));
        } catch {
          return cb(new Error(t('components.adminDashboard.createDomain.validation.urlInvalid')));
        }
      },
      trigger: 'blur'
    }
  ],
  colorSchemas: [
    {
      validator: (_, __, cb) => {
        const color = getSchemaHex('COLOR');
        const text = getSchemaHex('TEXT');
        if (!color) return cb(new Error(t('components.adminDashboard.createDomain.validation.pickPrimaryColor')));
        if (!text) return cb(new Error(t('components.adminDashboard.createDomain.validation.pickTextColor')));
        cb();
      },
      trigger: 'change'
    }
  ],
  supplierId: [
    { required: true, message: t('components.adminDashboard.createDomain.validation.supplierRequired'), trigger: 'change' }
  ],
  packagesIds: [
    {
      validator: (_, v, cb) =>
        (Array.isArray(v) && v.length > 0)
          ? cb()
          : cb(new Error(t('components.adminDashboard.createDomain.validation.packageRequired'))),
      trigger: 'change'
    }
  ],
  logo: [
    {
      validator: (_, v, cb) => (!!v ? cb() : cb(new Error(t('components.adminDashboard.createDomain.validation.logoRequired')))),
      trigger: ['change', 'blur']
    }
  ]
}));

function isHttpUrl(v: string) {
  try {
    const u = new URL(String(v));
    return /^(http|https):$/.test(u.protocol);
  } catch {
    return false;
  }
}

const isSubmitDisabled = computed(() => {
  const f = form.value;
  const us = (f.urlSuffix ?? '').trim();
  const urlSuffixOk = us.length > 0 && us.length <= 64 && /^[a-z0-9-]*$/i.test(us);
  const colorsOk = !!getSchemaHex('COLOR') && !!getSchemaHex('TEXT');
  const websiteOk = !f.companyWebsite || isHttpUrl(String(f.companyWebsite));
  const supplierOk = !!f.supplierId;
  const packagesOk = Array.isArray(f.packagesIds) && f.packagesIds.length > 0;
  const logoOk = !!f.logo;
  const busy = isProcessingImage.value || !!props.loading;
  return !(urlSuffixOk && colorsOk && websiteOk && supplierOk && packagesOk && logoOk) || busy;
});

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function onFileChange(file: UploadFile) {
  const raw = file.raw;
  if (!raw) return;
  const isImage = raw.type?.startsWith?.('image/');
  const isLt2M = raw.size ? raw.size / 1024 / 1024 < 2 : true;
  if (!isImage) { ElMessage?.warning?.(t('components.adminDashboard.createDomain.validation.imageOnly')); return; }
  if (!isLt2M) { ElMessage?.warning?.(t('components.adminDashboard.createDomain.validation.imageTooLarge')); return; }
  isProcessingImage.value = true;
  try {
    form.value.logo = await toBase64(raw);
    await nextTick();
    formRef.value?.validateField?.('logo');
  } catch {
    ElMessage?.error?.(t('components.adminDashboard.createDomain.validation.imageReadError'));
  } finally {
    isProcessingImage.value = false;
  }
}

function clearLogo() {
  form.value.logo = null;
  nextTick(() => formRef.value?.validateField?.('logo'));
}

async function onSubmit() {
  if (!getSchemaHex('COLOR')) setSchemaHex('COLOR', colorHex.value || '');
  if (!getSchemaHex('TEXT')) setSchemaHex('TEXT', textHex.value || '');
  const isValid = await formRef.value?.validate().catch(() => false);
  if (isValid) {
    wasSubmitting.value = true;
    emit('submit', form.value);
  }
}
</script>

<template>
  <el-form
    ref="formRef"
    :model="form"
    :rules="rules"
    label-width="140px"
    status-icon
  >
    <el-form-item :label="$t('components.adminDashboard.createDomain.labels.logo')" prop="logo" required>
      <el-upload
        :auto-upload="false"
        :show-file-list="false"
        accept="image/*"
        :on-change="onFileChange"
      >
        <el-button type="primary">{{ $t('components.adminDashboard.createDomain.buttons.chooseImage') }}</el-button>
        <template #tip>
          <div class="el-upload__tip">
            {{ $t('components.adminDashboard.createDomain.tips.imageUpload') }}
          </div>
        </template>
      </el-upload>
    </el-form-item>

    <el-form-item v-if="form.logo">
      <el-image
        :src="form.logo"
        fit="contain"
        style="width: 200px; height: 200px; border: 1px dashed var(--el-border-color);"
      />
      <el-button text type="danger" class="ml-3" @click="clearLogo">
        {{ $t('components.adminDashboard.createDomain.buttons.remove') }}
      </el-button>
    </el-form-item>

    <el-form-item :label="$t('components.adminDashboard.createDomain.labels.urlSuffix')" prop="urlSuffix" required>
      <el-input
        v-model="form.urlSuffix"
        :placeholder="$t('components.adminDashboard.createDomain.placeholders.urlSuffix')"
        clearable
      />
    </el-form-item>

    <el-form-item :label="$t('components.adminDashboard.createDomain.labels.companyWebsite')" prop="companyWebsite" required>
      <el-input
        v-model="form.companyWebsite"
        :placeholder="$t('components.adminDashboard.createDomain.placeholders.companyWebsite')"
        clearable
      />
    </el-form-item>

    <el-form-item :label="$t('components.adminDashboard.createDomain.labels.released')">
      <el-switch v-model="form.released" />
    </el-form-item>

    <el-form-item :label="$t('components.adminDashboard.createDomain.labels.supplier')" prop="supplierId" required>
      <el-select
        v-model="form.supplierId"
        filterable
        clearable
        :disabled="!props.suppliers?.length"
        :placeholder="props.suppliers?.length
            ? $t('components.adminDashboard.createDomain.placeholders.selectSupplier')
            : $t('components.adminDashboard.createDomain.placeholders.noSuppliers')"
        style="width: 320px"
      >
        <el-option
          v-for="s in (props.suppliers || [])"
          :key="s.userId"
          :label="s.firstname + ' ' + s.lastname"
          :value="s.userId"
        />
      </el-select>
    </el-form-item>

    <el-form-item :label="$t('components.adminDashboard.createDomain.labels.packages')" prop="packagesIds" required>
      <el-select
        v-model="form.packagesIds"
        multiple
        filterable
        clearable
        collapse-tags
        :disabled="!props.packages?.length"
        :placeholder="props.packages?.length
            ? $t('components.adminDashboard.createDomain.placeholders.selectPackages')
            : $t('components.adminDashboard.createDomain.placeholders.noPackages')"
        style="width: 420px"
      >
        <el-option
          v-for="p in (props.packages || [])"
          :key="p.packageId"
          :label="p.description"
          :value="p.packageId"
        />
      </el-select>
    </el-form-item>

    <el-form-item :label="$t('components.adminDashboard.createDomain.labels.primaryColor')" prop="colorSchemas" required>
      <el-color-picker v-model="colorHex" />
    </el-form-item>

    <el-form-item :label="$t('components.adminDashboard.createDomain.labels.textColor')" prop="colorSchemas" required>
      <el-color-picker v-model="textHex" />
    </el-form-item>

    <el-form-item>
      <el-button
        type="primary"
        :loading="props.loading"
        :disabled="isSubmitDisabled"
        @click="onSubmit"
      >
        {{ $t('components.adminDashboard.createDomain.buttons.save') }}
      </el-button>
      <el-button @click="router.push('/admin/domains')">
        {{ $t('components.adminDashboard.createDomain.buttons.cancel') }}
      </el-button>
    </el-form-item>
  </el-form>
</template>

<style scoped>
.ml-3 { margin-left: 0.75rem; }
</style>
